import React, { useState, useCallback } from 'react';
import axios from 'axios';
import useAuthStore from '../../store/authStore';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fmt = (n) => Number(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '-';

const PERIODS = [
  { value: 'daily',   label: 'Today' },
  { value: 'weekly',  label: 'This Week' },
  { value: 'monthly', label: 'This Month' },
  { value: 'custom',  label: 'Custom Range' },
];

const STATUS_COLORS = {
  Paid:    { bg: '#e6f4ea', color: '#1e7e34' },
  Pending: { bg: '#fff3cd', color: '#856404' },
  Partial: { bg: '#cce5ff', color: '#004085' },
};

export default function IPDBillingReport() {
  const { token } = useAuthStore();
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().split('T')[0];
  const [period, setPeriod]       = useState('monthly');
  const [from, setFrom]           = useState(today.slice(0, 7) + '-01');
  const [to, setTo]               = useState(today);
  const [statusFilter, setStatus] = useState('');
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [search, setSearch]       = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { period };
      if (period === 'custom') { params.from = from; params.to = to; }
      if (statusFilter) params.payment_status = statusFilter;
      const res = await axios.get(`${API}/ipd/billing/report`, { headers, params });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load report');
    } finally { setLoading(false); }
  }, [period, from, to, statusFilter, token]);

  const printReport = () => window.print();

  const filtered = data?.bills?.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return b.patient_name?.toLowerCase().includes(q) ||
           b.mrn?.toLowerCase().includes(q) ||
           b.bill_number?.toLowerCase().includes(q) ||
           b.diagnosis?.toLowerCase().includes(q);
  }) || [];

  const s = data?.summary || {};

  return (
    <div style={{ padding: '24px', fontFamily: 'Segoe UI, sans-serif', background: '#f5f6fa', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1a237e', fontSize: 22 }}>IPD Billing Report</h2>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>Revenue, collections and outstanding balances</p>
        </div>
        {data && (
          <button onClick={printReport} style={{ padding: '8px 18px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
            Print / Export
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <div>
          <label style={lbl}>Period</label>
          <select value={period} onChange={e => setPeriod(e.target.value)} style={sel}>
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        {period === 'custom' && <>
          <div>
            <label style={lbl}>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inp} />
          </div>
        </>}
        <div>
          <label style={lbl}>Payment Status</label>
          <select value={statusFilter} onChange={e => setStatus(e.target.value)} style={sel}>
            <option value="">All</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Partial">Partial</option>
          </select>
        </div>
        <button onClick={load} disabled={loading} style={{ padding: '8px 22px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, marginTop: 18 }}>
          {loading ? 'Loading...' : 'Generate Report'}
        </button>
      </div>

      {error && <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '10px 16px', color: '#856404', marginBottom: 16 }}>{error}</div>}

      {data && <>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 20 }}>
          <SummaryCard label="Total Billed"    value={`Rs. ${fmt(s.total_billed)}`}    color="#1976d2" sub={`${s.total_bills} bills`} />
          <SummaryCard label="Total Collected" value={`Rs. ${fmt(s.total_paid)}`}      color="#2e7d32" sub={`${s.paid_count} paid`} />
          <SummaryCard label="Pending Balance" value={`Rs. ${fmt(s.total_pending)}`}   color="#e65100" sub={`${s.pending_count} pending`} />
          <SummaryCard label="Total Advance"   value={`Rs. ${fmt(s.total_advance)}`}   color="#6a1b9a" sub="received" />
          <SummaryCard label="Total Discount"  value={`Rs. ${fmt(s.total_discount)}`}  color="#c62828" sub="given" />
        </div>

        {/* Revenue Breakdown */}
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <h4 style={{ margin: '0 0 14px', color: '#333', fontSize: 15 }}>Revenue Breakdown</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <BreakdownBar label="Room / Ward" value={s.room_revenue}       total={s.total_billed} color="#1976d2" />
            <BreakdownBar label="Lab"         value={s.lab_revenue}        total={s.total_billed} color="#388e3c" />
            <BreakdownBar label="Pharmacy"    value={s.pharmacy_revenue}   total={s.total_billed} color="#7b1fa2" />
            <BreakdownBar label="Radiology"   value={s.radiology_revenue}  total={s.total_billed} color="#f57c00" />
          </div>
        </div>

        {/* Daily Breakdown */}
        {data.daily_breakdown?.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <h4 style={{ margin: '0 0 14px', color: '#333', fontSize: 15 }}>Daily Breakdown</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={tbl}>
                <thead>
                  <tr style={{ background: '#f5f6fa' }}>
                    <Th>Date</Th><Th>Bills</Th><Th>Billed</Th><Th>Collected</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily_breakdown.map((d, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <Td>{fmtDate(d.date)}</Td>
                      <Td>{d.bills}</Td>
                      <Td>Rs. {fmt(d.billed)}</Td>
                      <Td><span style={{ color: '#2e7d32', fontWeight: 600 }}>Rs. {fmt(d.collected)}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Detail Table */}
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h4 style={{ margin: 0, color: '#333', fontSize: 15 }}>Bill Details ({filtered.length})</h4>
            <input placeholder="Search patient / MRN / bill #..." value={search} onChange={e => setSearch(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 260 }} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={tbl}>
              <thead>
                <tr style={{ background: '#f5f6fa' }}>
                  <Th>Bill #</Th><Th>Date</Th><Th>Patient</Th><Th>MRN</Th><Th>Diagnosis</Th>
                  <Th>Ward</Th><Th>Room</Th><Th>Lab</Th><Th>Pharmacy</Th><Th>Radiology</Th>
                  <Th>Subtotal</Th><Th>Advance</Th><Th>Balance</Th><Th>Mode</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={15} style={{ textAlign: 'center', padding: 30, color: '#999' }}>No bills found</td></tr>
                ) : filtered.map((b, i) => {
                  const sc = STATUS_COLORS[b.payment_status] || {};
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <Td><b>{b.bill_number}</b></Td>
                      <Td>{fmtDate(b.bill_date)}</Td>
                      <Td>{b.patient_name}</Td>
                      <Td style={{ color: '#1976d2' }}>{b.mrn}</Td>
                      <Td>{b.diagnosis || '-'}</Td>
                      <Td>{b.ward_name || '-'}</Td>
                      <Td>Rs. {fmt(b.room_charges)}</Td>
                      <Td>Rs. {fmt(b.lab_charges)}</Td>
                      <Td>Rs. {fmt(b.pharmacy_charges)}</Td>
                      <Td>Rs. {fmt(b.radiology_charges)}</Td>
                      <Td><b>Rs. {fmt(b.subtotal)}</b></Td>
                      <Td style={{ color: '#6a1b9a' }}>Rs. {fmt(b.advance_paid)}</Td>
                      <Td style={{ color: b.balance_due > 0 ? '#e65100' : '#2e7d32', fontWeight: 600 }}>Rs. {fmt(b.balance_due)}</Td>
                      <Td>{b.payment_mode || '-'}</Td>
                      <Td>
                        <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color }}>
                          {b.payment_status}
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
              {filtered.length > 0 && (
                <tfoot>
                  <tr style={{ background: '#f5f6fa', fontWeight: 700 }}>
                    <td colSpan={6} style={{ padding: '8px 12px', color: '#555' }}>TOTAL ({filtered.length} bills)</td>
                    <Td>Rs. {fmt(filtered.reduce((a,b) => a + Number(b.room_charges||0), 0))}</Td>
                    <Td>Rs. {fmt(filtered.reduce((a,b) => a + Number(b.lab_charges||0), 0))}</Td>
                    <Td>Rs. {fmt(filtered.reduce((a,b) => a + Number(b.pharmacy_charges||0), 0))}</Td>
                    <Td>Rs. {fmt(filtered.reduce((a,b) => a + Number(b.radiology_charges||0), 0))}</Td>
                    <Td>Rs. {fmt(filtered.reduce((a,b) => a + Number(b.subtotal||0), 0))}</Td>
                    <Td>Rs. {fmt(filtered.reduce((a,b) => a + Number(b.advance_paid||0), 0))}</Td>
                    <Td>Rs. {fmt(filtered.reduce((a,b) => a + Number(b.balance_due||0), 0))}</Td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </>}

      {!data && !loading && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
          <p style={{ fontSize: 15 }}>Select a period and click Generate Report</p>
        </div>
      )}

      <style>{`
        @media print {
          button, input[type="date"], select { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '16px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function BreakdownBar({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((Number(value) / Number(total)) * 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
        <span style={{ color: '#555' }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{pct}%</span>
      </div>
      <div style={{ background: '#f0f0f0', borderRadius: 4, height: 8 }}>
        <div style={{ width: `${pct}%`, background: color, height: 8, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>Rs. {Number(value||0).toLocaleString()}</div>
    </div>
  );
}

const lbl = { display: 'block', fontSize: 12, color: '#666', marginBottom: 4, fontWeight: 600 };
const sel = { padding: '7px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, minWidth: 130 };
const inp = { padding: '7px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 };
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const Th = ({ children }) => <th style={{ padding: '8px 12px', textAlign: 'left', color: '#555', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{children}</th>;
const Td = ({ children, style }) => <td style={{ padding: '8px 12px', color: '#333', whiteSpace: 'nowrap', ...style }}>{children}</td>;
